import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";

import { PageShell } from "../components/PageShell.js";
import { Seo } from "../components/Seo.js";

export function Spirit() {
  const { hash } = useLocation();

  useEffect(() => {
    if (!hash) {
      window.scrollTo(0, 0);
      return;
    }
    // Instant (not smooth) scroll: on client-side navigation the component
    // re-renders a few times and a smooth animation gets interrupted, so it
    // never lands on the target. An instant jump is reliable.
    document.getElementById(hash.slice(1))?.scrollIntoView({ block: "start" });
  }, [hash]);

  return (
    <PageShell>
      <Seo
        title="The Spirit of JivaSeva · JivaSeva"
        description="The universal spirit behind JivaSeva — Life Takes Care of Life: the expanding circle of care and the shared human heritage of selfless service that inspires our mission."
        path="/spirit"
      />
      <h1 className="page-heading">The Spirit of JivaSeva</h1>

      <section className="about-section">
        <p className="manifesto">
          <span className="brand-name">JivaSeva</span> is inspired by a simple
          yet profound understanding: <em>Life Takes Care of Life.</em> — the
          understanding that the well-being of each depends upon the well-being
          of all. To truly understand our mission, we must look at the universal
          spirit that inspires it, the natural expansion of our circle of care,
          and the collective stewardship required to sustain it.
        </p>
        <p className="manifesto">
          Although cultures, traditions, and philosophies may express this truth
          in different ways, the underlying spirit is universal. It transcends
          race, religion, nationality, language, culture, and social background.
          It belongs to no single community because it belongs to all.
        </p>
        <p className="manifesto">
          Across the world's great religions, philosophical traditions, and
          spiritual teachings, we find a common invitation: to care for one
          another, to share what we have, to act with compassion, and to
          recognize our interconnectedness. The same values are embraced by many
          humanists, naturalists, secular thinkers, scientists, and people of
          goodwill who see compassion, cooperation, and service as essential to
          human flourishing.
        </p>
        <p className="manifesto">
          JivaSeva does not seek to promote any one belief system. Instead, it
          celebrates this shared human heritage and the universal spirit of
          selfless service that has inspired people across generations and
          civilizations.
        </p>
        <p id="quotations" className="manifesto">
          To help explore this shared heritage, we have compiled{" "}
          <Link to="/spirit/quotations" className="inline-link">
            a growing collection of quotations
          </Link>
          , teachings, and reflections drawn from religions, philosophies,
          social reformers, spiritual teachers, and humanitarian leaders. We hope
          these resources inspire deeper reflection and encourage meaningful
          action.
        </p>
        <p className="manifesto">
          If you know of a passage, teaching, or perspective that beautifully
          expresses this spirit, we would be delighted to hear from you through
          our{" "}
          <Link to="/contact" className="inline-link">
            Contact page
          </Link>
          .
        </p>
      </section>

      <section className="about-section">
        <h2 className="section-heading">The Expanding Circle of Care</h2>
        <p className="manifesto">
          Every living being naturally begins by caring for itself. As life
          matures, that circle of care expands.
        </p>
        <p className="manifesto">
          We first care for our family, then our friends and neighbors, our
          communities, and the societies in which we live. As our awareness
          deepens, that concern naturally extends beyond the boundaries of
          nationality, culture, and belief until it embraces all life.
        </p>
        <p className="manifesto">
          This gradual expansion of compassion is reflected throughout human
          history. It has been described by philosophers, spiritual teachers,
          scientists, poets, and social leaders in many different ways, yet the
          underlying movement is remarkably similar: an ever-widening
          recognition that our own well-being is inseparable from the well-being
          of others.
        </p>
        <p className="manifesto">
          JivaSeva exists to nurture and support this natural expansion by
          helping remove the practical barriers that often prevent compassionate
          action. This organic growth from "me" to "we" is the wellspring from
          which JivaSeva flows. True fulfillment arrives when we realize that our
          individual well-being cannot be isolated from the whole.
        </p>
      </section>

      <section className="about-section">
        <h2 className="section-heading">An Initiative for Everyone</h2>
        <p className="manifesto">
          JivaSeva belongs to everyone who believes in the spirit of selfless
          service.
        </p>
        <p className="manifesto">
          It is not the work of a single individual, nor does it belong to any
          one organization or community. Every volunteer, supporter,
          collaborator, and well-wisher helps shape its future through their
          ideas, service, and commitment. JivaSeva is an initiative that belongs
          to everyone who wishes to contribute to its spirit. We encourage
          communities to take ownership of this spirit, discover localized needs,
          and actively find unique ways to promote selfless service.
        </p>
        <p className="manifesto">
          We welcome anyone who shares this vision to participate in whatever way
          they are able—whether by volunteering, contributing professional
          skills, supporting projects, sharing ideas, or simply helping spread
          the message that <em>Life Takes Care of Life</em>.
        </p>
        <p className="manifesto">
          As JivaSeva grows, new services, initiatives, and opportunities for
          service will continue to emerge. We believe that many of the best ideas
          will come not from a central organization, but from the community
          itself.
        </p>
        <p className="manifesto">
          To ensure that every initiative remains faithful to JivaSeva's
          founding vision and guiding principles, the initiative is supported by
          a volunteer-led JivaSeva Core Committee. The Core Committee helps
          steward the long-term mission of JivaSeva, helps guide new initiatives
          proposed under the JivaSeva name, and helps ensure that all activities
          continue to reflect the values upon which JivaSeva was founded.
        </p>
        <p className="manifesto">
          If you would like to contribute to JivaSeva, propose an initiative, or
          learn more about participating, we would love to hear from you through
          our{" "}
          <Link to="/contact" className="inline-link">
            Contact page
          </Link>
          .
        </p>
      </section>
    </PageShell>
  );
}
